import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
} from "@mui/material";
import { fontStyles } from "components/Common/commonStyles";
import ClassRosterDetailDialog from "components/LearnPoint/DTR/ClassRosterDetailDialog";
import ViewDtrsDialog from "components/LearnPoint/DTR/ViewDtrsDialog";
import {
  getClassRosterProjectDetails,
  getClassRosterSiteDetails,
  getDTRListForRoster,
} from "services/learnPointUserServices";

// ASTT-7319: one row per selected roster, replacing the previous
// Label:Value layout's comma-concatenation of every selected roster's
// data into a single row/value. Backed by the new, additive
// getClassDetailsForDTRPerRoster endpoint.
//
// Day 1 Show is a single column with a per-row label, not two separate
// TTF/Product Training columns -- each roster row can have its own TTFNA
// value now (unlike the old combined display, which only had one
// TTFNA for the whole selection), so which count applies can genuinely
// differ row to row.
//
// ASTT-7319 follow-up (restructured after real testing surfaced a real
// bug): Parent Client/Client/Program/Project/Site Location/Country are
// NOT columns here anymore -- a roster spanning multiple Client/Program/
// Project combinations was producing duplicate main rows, since that
// data was joined directly into the per-roster query. Moved to two
// on-demand drill-down dialogs ("View Projects"/"View Site" buttons per
// row) instead, which also guarantees exactly one row per roster here.
const COLUMNS = [
  { id: "ClassRosterId", label: "Roster ID" },
  { id: "Wave", label: "Wave" },
  { id: "ClassType", label: "Class Type" },
  { id: "ClassStatus", label: "Class Status" },
  { id: "CurriculumStatus", label: "Curriculum Status" },
  // ASTT-7319 follow-up: labels shortened per direct stakeholder-demo
  // feedback (was "TTF Start Date"/"Product Training Start Date"/
  // "Nesting Start Date"/"Production Start Date").
  { id: "TTFDateTime", label: "TTF Start" },
  { id: "ProductTrainingDateTime", label: "Product Training Start" },
  { id: "NestingStartDateTime", label: "Nesting Start" },
  { id: "ProductionStartDate", label: "Production Start" },
  { id: "day1Show", label: "Day 1 Show" },
  // ASTT-7319 follow-up: align: "right" added to every genuinely numeric
  // column, per direct stakeholder-demo feedback -- read by both the
  // header and body cell rendering below, so the two can't drift out of
  // sync with each other.
  { id: "ActiveTraineeCount", label: "Active Head Count", align: "right" },
  { id: "GraduatedHeadCount", label: "Graduated Head Count", align: "right" },
  { id: "TerminatedCount", label: "Termed", align: "right" },
  { id: "ClientIdReadiness", label: "Client ID Readiness", align: "right" },
  { id: "TtecIdReadiness", label: "TTEC ID Readiness", align: "right" },
  { id: "EquipmentReadiness", label: "Equipment Readiness", align: "right" },
];

const PROJECT_DIALOG_COLUMNS = [
  { id: "ParentClient", label: "Parent Client" },
  { id: "Client", label: "Client" },
  { id: "Program", label: "Program" },
  { id: "Project", label: "Project" },
];

const SITE_DIALOG_COLUMNS = [
  { id: "SiteLocation", label: "Site Location" },
  { id: "Country", label: "Country" },
];

// ASTT-7319 follow-up: DTR_DIALOG_COLUMNS removed -- "View DTRs" moved to
// its own dedicated ViewDtrsDialog.js (pagination/filter/sort), which
// defines its own columns internally.

const formatDay1Show = (row) => {
  const isTTFNA = row?.TTFNA === true || row?.TTFNA === 1;
  const label = isTTFNA ? "Product Training" : "TTF";
  const value = isTTFNA ? row?.ProductTrainingDayOneCount : row?.TTFDayOneCount;
  return `${label}: ${value ?? 0}`;
};

// ASTT-7319: the backend returns the literal string "NULL" for a genuinely
// empty date (see the stored procedure's own REPLACE(...,'1900-01-01...',
// 'NULL') pattern) rather than an actual null -- checked for explicitly
// here so dayjs never tries to parse that literal string as a date
// (which would otherwise silently produce "Invalid Date").
// ASTT-7319 follow-up: returns "N/A" now, not the literal "NULL" string --
// per direct request that no column should ever display the word "NULL"
// to the user.
const formatDateValue = (value, format) => {
  if (!value || value === "NULL") return "N/A";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : value;
};

// ASTT-7319 follow-up: defaultProps replaced with JS default parameters
// directly in the signature -- React's defaultProps support for function
// components is deprecated (confirmed via a real console warning), this
// is the migration path React itself documents.
export default function ClassRosterDetailsTable({ rows = [], loading = false, onJumpToDtrDate }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKind, setDialogKind] = useState(null); // "projects" | "site"
  const [dialogRows, setDialogRows] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogRosterId, setDialogRosterId] = useState(null);

  // ASTT-7319 follow-up: "View DTRs" now has its own separate dialog
  // state (dtrsDialogOpen/dtrsRows/dtrsLoading/dtrsRosterId), rendered via
  // ViewDtrsDialog.js rather than sharing dialogOpen/dialogRows above
  // with View Projects/View Site -- keeps the two dialogs' state fully
  // independent now that they're genuinely different components.
  const [dtrsDialogOpen, setDtrsDialogOpen] = useState(false);
  const [dtrsRows, setDtrsRows] = useState([]);
  const [dtrsLoading, setDtrsLoading] = useState(false);
  const [dtrsRosterId, setDtrsRosterId] = useState(null);

  // ASTT-7319 follow-up: per-roster Project/Site/DTR counts, shown on
  // the action buttons themselves ("View Projects (N)"), per direct
  // stakeholder-demo feedback. The main per-roster query these rows come
  // from doesn't currently return these counts (confirmed -- it's a
  // dynamic Dapper passthrough of whatever the stored procedure selects,
  // and no such field exists there today), so this reuses the same
  // three lightweight endpoints the on-demand dialogs already call,
  // fetched upfront for whichever rosters are currently selected instead
  // of waiting for each button's own click. Worth knowing: with the
  // 5-roster selection cap already in place, this is at most 15 extra
  // requests per roster-selection change (5 rosters x 3 endpoints) --
  // fine at that cap, but would need moving server-side into the main
  // query if the cap ever grows meaningfully.
  const [rosterCounts, setRosterCounts] = useState({});

  useEffect(() => {
    let cancelled = false;

    const fetchCounts = async () => {
      const results = await Promise.all(
        rows.map(async (row) => {
          const classRosterId = row.ClassRosterId;
          const [projectsRes, sitesRes, dtrsRes] = await Promise.all([
            getClassRosterProjectDetails(classRosterId),
            getClassRosterSiteDetails(classRosterId),
            getDTRListForRoster(classRosterId),
          ]);
          return {
            classRosterId,
            projects:
              projectsRes?.statusCode === 200 ? (projectsRes?.data ?? []).length : 0,
            sites: sitesRes?.statusCode === 200 ? (sitesRes?.data ?? []).length : 0,
            dtrs: dtrsRes?.statusCode === 200 ? (dtrsRes?.data ?? []).length : 0,
          };
        }),
      );
      if (cancelled) return;
      const nextCounts = {};
      results.forEach((r) => {
        nextCounts[r.classRosterId] = r;
      });
      setRosterCounts(nextCounts);
    };

    if (rows.length > 0) {
      fetchCounts();
    } else {
      setRosterCounts({});
    }

    return () => {
      cancelled = true;
    };
  }, [rows]);

  const handleViewProjects = async (classRosterId) => {
    setDialogKind("projects");
    setDialogRosterId(classRosterId);
    setDialogOpen(true);
    setDialogLoading(true);
    try {
      const response = await getClassRosterProjectDetails(classRosterId);
      setDialogRows(response?.statusCode === 200 ? (response?.data ?? []) : []);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleViewSite = async (classRosterId) => {
    setDialogKind("site");
    setDialogRosterId(classRosterId);
    setDialogOpen(true);
    setDialogLoading(true);
    try {
      const response = await getClassRosterSiteDetails(classRosterId);
      setDialogRows(response?.statusCode === 200 ? (response?.data ?? []) : []);
    } finally {
      setDialogLoading(false);
    }
  };

  // ASTT-7319 follow-up: dates kept raw (backend's own value, an ISO-ish
  // datetime string) rather than pre-formatted here -- ViewDtrsDialog.js
  // needs real dayjs comparisons for its own filtering/sorting, which
  // formatted "MM/DD/YYYY" strings can't support correctly (they compare
  // as plain text, breaking across year boundaries). Formatting for
  // display now happens only inside ViewDtrsDialog.js's own render.
  const handleViewDtrs = async (classRosterId) => {
    setDtrsRosterId(classRosterId);
    setDtrsDialogOpen(true);
    setDtrsLoading(true);
    try {
      const response = await getDTRListForRoster(classRosterId);
      setDtrsRows(response?.statusCode === 200 ? (response?.data ?? []) : []);
    } finally {
      setDtrsLoading(false);
    }
  };

  // Fired when a DTR row's action button is clicked -- jumps DTR Date/
  // loads that entry immediately, without the Load button, per direct
  // request. row.DTRDate here is the raw backend value now (see
  // handleViewDtrs above); TrainingNotes.js's jumpToDtrDate effect parses
  // it via dayjs's native ISO handling, not a manual format-specific split.
  const handleSelectDtr = (row) => {
    onJumpToDtrDate?.(row.DTRDate);
    closeDtrsDialog();
  };

  const closeDtrsDialog = () => {
    setDtrsDialogOpen(false);
    setDtrsRows([]);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogRows([]);
  };

  return (
    <Box sx={fontStyles}>
      {/* ASTT-7319: 16 columns is still wider than most viewports (down
          from 22, now that Projects/Site moved to drill-downs) --
          overflowX: "auto" on the container plus a minWidth on the Table
          itself forces a real horizontal scrollbar rather than letting
          columns squeeze illegibly narrow to fit. */}
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1800 }}>
          <TableHead>
            <TableRow>
              {/* ASTT-7319: Actions column moved to the front, per direct
                  request -- was the last column. */}
              <TableCell sx={{ fontWeight: 700, backgroundColor: "#EAF3FF", whiteSpace: "nowrap" }}>
                Actions
              </TableCell>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{ fontWeight: 700, backgroundColor: "#EAF3FF", whiteSpace: "nowrap" }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const counts = rosterCounts[row.ClassRosterId];
              return (
                <TableRow key={row.ClassRosterId} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {/* ASTT-7319 follow-up: counts + bold, per direct
                        stakeholder-demo feedback -- counts come from
                        rosterCounts (undefined until that fetch resolves,
                        so the "(N)" suffix simply doesn't render yet
                        rather than showing a misleading "(0)"). */}
                    <Button
                      size="small"
                      onClick={() => handleViewProjects(row.ClassRosterId)}
                      sx={{ textTransform: "none", fontWeight: 700, mr: 1 }}
                    >
                      View Projects{counts && ` (${counts.projects})`}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleViewSite(row.ClassRosterId)}
                      sx={{ textTransform: "none", fontWeight: 700, mr: 1 }}
                    >
                      View Sites{counts && ` (${counts.sites})`}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleViewDtrs(row.ClassRosterId)}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      View DTRs{counts && ` (${counts.dtrs})`}
                    </Button>
                  </TableCell>
                  <TableCell>{row.ClassRosterId}</TableCell>
                  <TableCell>{row.Wave ?? "N/A"}</TableCell>
                  <TableCell>{row.ClassType ?? "N/A"}</TableCell>
                  <TableCell>{row.ClassStatus ?? "N/A"}</TableCell>
                  <TableCell>{row.CurriculumStatus ?? "N/A"}</TableCell>
                  {/* ASTT-7319 follow-up: TTF Start formatted date-only now
                      (was "MM/DD/YYYY hh:mm A"), per direct stakeholder-demo
                      feedback -- Product Training/Nesting still show their
                      time component, since only TTF was called out. */}
                  <TableCell>{formatDateValue(row.TTFDateTime, "MM/DD/YYYY")}</TableCell>
                  <TableCell>{formatDateValue(row.ProductTrainingDateTime, "MM/DD/YYYY hh:mm A")}</TableCell>
                  <TableCell>{formatDateValue(row.NestingStartDateTime, "MM/DD/YYYY hh:mm A")}</TableCell>
                  <TableCell>{formatDateValue(row.ProductionStartDate, "MM/DD/YYYY")}</TableCell>
                  <TableCell>{formatDay1Show(row)}</TableCell>
                  <TableCell align="right">{row.ActiveTraineeCount ?? 0}</TableCell>
                  <TableCell align="right">{row.GraduatedHeadCount ?? 0}</TableCell>
                  <TableCell align="right">{row.TerminatedCount ?? 0}</TableCell>
                  <TableCell align="right">{row.ClientIdReadiness ?? 0}</TableCell>
                  <TableCell align="right">{row.TtecIdReadiness ?? 0}</TableCell>
                  <TableCell align="right">{row.EquipmentReadiness ?? 0}</TableCell>
                </TableRow>
              );
            })}
            {/* ASTT-7319: implements the UX suggestion from the previous
                thread -- loading was only used to suppress the empty-
                state message, with no actual visible feedback while a
                fetch was in flight (a blank table, or briefly stale rows
                from the previous selection). This row makes that wait
                visible. */}
            {loading && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 3, color: "#9e9e9e" }}>
                  No roster selected.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ClassRosterDetailDialog
        open={dialogOpen}
        onClose={closeDialog}
        title={
          dialogKind === "projects"
            ? `Projects — Roster ${dialogRosterId}`
            : `Site — Roster ${dialogRosterId}`
        }
        columns={dialogKind === "projects" ? PROJECT_DIALOG_COLUMNS : SITE_DIALOG_COLUMNS}
        rows={dialogRows}
        loading={dialogLoading}
      />

      {/* ASTT-7319 follow-up: "View DTRs" now uses its own dedicated
          dialog (pagination/filter/sort), separate from the generic
          drill-down dialog above used by View Projects/View Site. */}
      <ViewDtrsDialog
        open={dtrsDialogOpen}
        onClose={closeDtrsDialog}
        rosterId={dtrsRosterId}
        rows={dtrsRows}
        loading={dtrsLoading}
        onSelectDtr={handleSelectDtr}
      />
    </Box>
  );
}

ClassRosterDetailsTable.propTypes = {
  rows: PropTypes.array,
  loading: PropTypes.bool,
  onJumpToDtrDate: PropTypes.func,
};
